import { createFileRoute, Navigate } from '@tanstack/react-router';
import { Fragment, useEffect, useRef } from 'react';

import {
    DEFAULT_AUTO_DISMISS_AFTER_DAYS,
    NotificationAutoCleanupSettings,
} from '@apps/web/components/settings/notification_auto_cleanup_settings';
import { NotificationChannelHub } from '@apps/web/components/settings/notification_channel_hub';
import { NotificationRoutingCard } from '@apps/web/components/settings/notification_routing_card';
import { OfficeAssignmentCard } from '@apps/web/components/settings/office_assignment_card';
import {
    SettingsRow,
    SettingsSection,
} from '@apps/web/components/settings/settings_primitives';
import { SlackSubscriptionsSection } from '@apps/web/components/settings/slack_subscriptions_section';
import { useNotificationSettingsData } from '@apps/web/components/settings/use_notification_settings_data';
import { Button } from '@apps/web/components/ui/button';
import {
    SettingsPageSections,
    SettingsPageShell,
    useSettingsGates,
} from '@apps/web/features/settings';
import { cn } from '@apps/web/lib/utils';

export const Route = createFileRoute('/_app/settings/notifications/')({
    component: NotificationsHubPage,
});

function NotificationsHubPage() {
    const { organizationId, gates } = useSettingsGates();
    const {
        highlight,
        organizationId: searchOrganizationId,
        source,
        unsubscribeEmailType,
    } = Route.useSearch();
    const {
        handleAutoCleanupUpdate,
        handleReset,
        isLoading,
        isSmsNumberConfigured,
        notificationPreferences,
        sections,
    } = useNotificationSettingsData(organizationId);
    const autoCleanupSectionRef = useRef<HTMLDivElement | null>(null);
    const didHighlight = useRef(false);

    useEffect(() => {
        if (highlight !== 'auto-cleanup' || isLoading || didHighlight.current) {
            return;
        }
        const timeoutId = window.setTimeout(() => {
            didHighlight.current = true;
            autoCleanupSectionRef.current?.scrollIntoView({
                behavior: 'smooth',
                block: 'center',
            });
        }, 600);
        return () => window.clearTimeout(timeoutId);
    }, [highlight, isLoading]);

    if (unsubscribeEmailType) {
        return (
            <Navigate
                to="/settings/notifications/$channel"
                params={{ channel: 'email' }}
                search={{
                    highlight,
                    organizationId: searchOrganizationId,
                    source,
                    unsubscribeEmailType,
                }}
                replace={true}
            />
        );
    }

    return (
        <SettingsPageShell title="Notifications">
            <SettingsPageSections>
                <NotificationChannelHub
                    isLoading={isLoading}
                    isSmsNumberConfigured={isSmsNumberConfigured}
                    preferences={notificationPreferences}
                    sections={sections}
                />
                {organizationId !== null && gates.multiOffice ? (
                    <OfficeAssignmentCard
                        organizationId={organizationId}
                        variant="embedded"
                    />
                ) : null}
                <div
                    ref={autoCleanupSectionRef}
                    data-testid="auto-cleanup-section"
                    data-highlighted={highlight === 'auto-cleanup' || undefined}
                    className={cn(
                        'rounded-lg transition-shadow',
                        highlight === 'auto-cleanup' &&
                            'ring-primary ring-2 ring-offset-4',
                    )}
                >
                    <SettingsSection
                        title="Automatic cleanup"
                        description="Phoebe tidies notifications you don’t use. Every automatic behavior can be turned off here."
                    >
                        <NotificationAutoCleanupSettings
                            autoDisableUnusedEnabled={
                                notificationPreferences?.auto_disable_unused_inapp_notifications ??
                                true
                            }
                            autoDismissEnabled={
                                notificationPreferences?.auto_dismiss_notifications_enabled ??
                                true
                            }
                            autoDismissAfterDays={
                                notificationPreferences?.auto_dismiss_notifications_after_days ??
                                DEFAULT_AUTO_DISMISS_AFTER_DAYS
                            }
                            onUpdate={(body, analytics) =>
                                void handleAutoCleanupUpdate(body, analytics)
                            }
                        />
                    </SettingsSection>
                </div>
                {organizationId !== null ? (
                    <Fragment>
                        <NotificationRoutingCard
                            careCoordinatorFeatureEnabled={
                                gates.careCoordinator
                            }
                            displayMode="section"
                            redesign={true}
                            organizationId={organizationId}
                        />
                        <SlackSubscriptionsSection
                            organizationId={organizationId}
                        />
                    </Fragment>
                ) : null}
                <SettingsSection>
                    <SettingsRow
                        title="Reset to defaults"
                        description="Reset all available notification preferences to their default settings"
                        control={
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                    void handleReset();
                                }}
                            >
                                Reset
                            </Button>
                        }
                    />
                </SettingsSection>
            </SettingsPageSections>
        </SettingsPageShell>
    );
}
